import { setGrowthBookUser } from '@/plugins/useGrowthbook'
import type { ProgressMilestoneUpdateEvent } from '@/reward-types'
import fetcher from '@/services/fetcher'
import { RealTimeEvents } from '@/services/real-time-events'
import { RewardToastSystem } from '@/services/reward-toast-service'
import type {
  AccountStatus,
  BalanceSummary,
  BalanceSummaryResponse,
  ClaimType,
  CreditBalanceResponse,
  CsiResponse,
  DailySpinProgress,
  DailySpinProgressResponse,
  EntryDetailItem,
  EntryDetailResponse,
  GameSessionData,
  ImpersonationMode,
  InstantWinDraw,
  InstantWinDrawTickets,
  LoginResponse,
  Membership,
  Nullable,
  PaginatedCreditHistory,
  PasswordReset,
  Plan,
  Prize,
  PrizeFulfilment,
  PrizeFulfilmentWithPrize,
  Ticket,
  User,
} from '@/types'
import {
  Countries,
  CreditTypes,
  MembershipPeriods,
  PaymentProviders,
  SubscriptionStatus,
  type TMembershipPeriodValue,
  type TPlanTier,
  type TSubscriptionStatus,
} from '@/utils/helper-objects'
import { isPrize, isUser } from '@/utils/type-guard-utils'
import { toApiUrl, toPortalUrl } from '@/utils/url-generation-utils'
import { delay, nextAvailablePlan } from '@/utils/utils'
import { useCookies } from '@vueuse/integrations/useCookies'
import { DateTime, Interval } from 'luxon'
import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'
import { useToast } from 'vue-toastification'

import { useCustomerSafetyStore } from './customer-safety'
import { useFeatureFlagsStore } from './feature-flags'
import { useInstantWinsStore } from './instant-wins'
import { useNotificationStore } from './notification'
import { usePlansStore } from './plans'

// Re-export the auth watchers composable for convenience
export { useAuthWatchers } from '@/composables/useAuthWatchers'

const EmptyDailyGameProgress = (): Array<DailySpinProgress> => {
  const emptyDailyGameProgress = []

  const days = Interval.fromDateTimes(
    DateTime.now().startOf('week'),
    DateTime.now().endOf('week')
  ).splitBy({ days: 1 })

  for (const [i, day] of Object.entries(days)) {
    emptyDailyGameProgress.push({
      day_number: Number(i) + 1,
      day_name: day.start?.toFormat('EEE') ?? '',
      progress: 0,
      is_played: false,
    })
  }

  return emptyDailyGameProgress
}

// https://runthatline.com/pinia-typescript-type-state-actions-getters/
export type UserStoreState = {
  /**
   * The currently logged in user.
   */
  user: User | null
  /**
   * Paginated user credit history.
   */
  creditHistory: Map<number, PaginatedCreditHistory>
  latestInstantWinEntries: Map<string, InstantWinDrawTickets>
  dailyGameProgress: DailySpinProgress[]
  prizeFulfilments: Map<string, PrizeFulfilmentWithPrize>
  impersonationMode: ImpersonationMode | null
}

export const useUserStore = defineStore('user', {
  state: (): UserStoreState => ({
    user: null,
    creditHistory: new Map(),
    latestInstantWinEntries: new Map(),
    dailyGameProgress: EmptyDailyGameProgress(),
    prizeFulfilments: new Map(),
    impersonationMode: null,
  }),

  getters: {
    isLoggedIn({ user }): boolean {
      return isUser(user)
    },
    isLoggedOut(): boolean {
      return !this.isLoggedIn
    },
    // TODO This should be a new clone every time (in case its called multiple times)
    newEmptyUser(): Ref<User> {
      return ref<User>({
        __type: 'User',
        city: '',
        country: Countries.UnitedKingdom,
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        phone: '',
        post_code: '',
        referral_link: '',
        referrals: [],
        referrer: false,
        region: '',
        status: '',
        address_line_1: '',
        address_line_2: '',
        subscriber_id: undefined,
        subscriptions: [],
        tickets: {
          draws: {},
        },
        consent: false,
        contact_optin: false,
        credit: {
          has_history: false,
          totals: {
            overall: 0,
            cashable: 0,
            bonus: 0,
            instant_win_cashable: 0,
          },
        },
        loyalty: {
          total: {
            credits: 0,
            entries: 0,
          },
          subscriptions: {},
          extras: [],
        },
        tier: {
          in_trial: false,
          loyalty_credit: 0,
          loyalty_entries: 0,
          referral_discount: 0,
          referral_credit: 0,
          processing: false,
          period: MembershipPeriods.Monthly,
          plan_tier: 'standard',
          platform: 'chargebee_v1',
        },
        eligible_points: 0,
        user_progression: {
          rank: 0,
          level: 0,
          xp_earned: 0,
          cash_earned: {
            saving_currency_code: this.$settings.activeCurrency,
            total_saved: 0,
          },
        },
        frequency_game: {
          weekly_progress: [],
        },
        payment_methods: {},
        player_safety: {},
      })
    },
    drawTickets(): Nullable<Record<string, Ticket>> {
      return this.user?.tickets?.draws ?? null
    },
    totalDrawTicketCount(): number {
      return Object.values(this.drawTickets ?? {}).reduce(
        (acc, ticket: Ticket) => acc + (ticket.count ?? 0),
        0
      )
    },
    subscriptionTicketCount(): number {
      return (
        this.user?.subscriptions?.reduce(
          (acc: number, subscription: Membership) => {
            return acc + subscription.plan_tickets
          },
          0
        ) ?? 0
      )
    },
    isInTrial(): boolean {
      return this.user?.tier?.in_trial ?? false
    },
    highestPlanTier(): Nullable<TPlanTier> {
      return this.user?.tier?.plan_tier ?? null
    },
    highestPlanPeriod(): Nullable<TMembershipPeriodValue> {
      return this.user?.tier?.period ?? null
    },
    highestPlan(): Nullable<Plan> {
      const plansStore = usePlansStore()

      // Find subscription matching both tier and period
      const matchingSubscription = this.user?.subscriptions?.find(
        (subscription) =>
          subscription.plan_tier === this.highestPlanTier &&
          subscription.plan_period === this.highestPlanPeriod
      )

      if (!matchingSubscription) {
        return null
      }

      // Find plan by subscription's code
      return (
        plansStore.allPlansArray.find(
          (plan) =>
            plan.code.toLowerCase() === matchingSubscription.code.toLowerCase()
        ) ?? null
      )
    },
    upsellToPlan(): Nullable<Plan> {
      const plansStore = usePlansStore()

      if (!this.highestPlanTier || !this.highestPlanPeriod) {
        return null
      }

      const nextAvailablePlanTiers = nextAvailablePlan(this.highestPlanTier)

      if (
        !nextAvailablePlanTiers ||
        this.highestPlanTier === nextAvailablePlanTiers
      ) {
        return null
      }

      // Try each next tier plan until we find one that exists
      return plansStore.getPlanByTier(
        nextAvailablePlanTiers,
        this.highestPlanPeriod
      )
    },
    /**
     * Gets the VIP plan based off the user's current plan period.
     *
     * @returns {Nullable<Plan>} The VIP plan or null if not found.
     */
    vipPlan(): Nullable<Plan> {
      const plansStore = usePlansStore()

      return plansStore.getPlanByTier('vip', this.highestPlan?.period)
    },
    /**
     * Gets the Standard plan based off the user's current plan period.
     *
     * @returns {Nullable<Plan>} The Standard plan or null if not found.
     */
    standardPlan(): Nullable<Plan> {
      const plansStore = usePlansStore()

      return plansStore.getPlanByTier('standard', this.highestPlan?.period)
    },
    /**
     * Gets the Platinum plan based off the user's current plan period.
     *
     * @returns {Nullable<Plan>} The Platinum plan or null if not found.
     */
    platinumPlan(): Nullable<Plan> {
      const plansStore = usePlansStore()

      return plansStore.getPlanByTier('platinum', this.highestPlan?.period)
    },
    /**
     * Gets the Premium plan based off the user's current plan period.
     *
     * @returns {Nullable<Plan>} The Premium plan or null if not found.
     */
    premiumPlan(): Nullable<Plan> {
      const plansStore = usePlansStore()

      return plansStore.getPlanByTier('premium', this.highestPlan?.period)
    },
    /**
     * Gets the Legend plan based off the user's current plan period.
     *
     * @returns {Nullable<Plan>} The Legend plan or null if not found.
     */
    legendPlan(): Nullable<Plan> {
      const plansStore = usePlansStore()

      return plansStore.getPlanByTier('legend', this.highestPlan?.period)
    },
    showUpgradePlan(): boolean {
      return this.isLoggedIn && !!this.highestPlan && !!this.upsellToPlan
    },
    isImpersonating(): boolean {
      return this.isLoggedIn && this.impersonationMode === 'read_only'
    },
    hasSubscription(): boolean {
      return this.isLoggedIn && !!this.user?.subscriptions?.length
    },
    hasActiveSubscription(): boolean {
      if (!this.isLoggedIn) {
        return false
      }

      const activeStatuses: TSubscriptionStatus[] = [
        SubscriptionStatus.Active,
        SubscriptionStatus.InTrial,
        SubscriptionStatus.NonRenewing,
      ]

      return (
        this.user?.subscriptions?.some((subscription) =>
          activeStatuses.includes(subscription.status_raw)
        ) ?? false
      )
    },
    hasActiveNonSolidgateSubscription(): boolean {
      return (
        this.hasActiveSubscription &&
        this.user?.tier?.platform !== PaymentProviders.Solidgate
      )
    },
    /**
     * Determines whether to use Solidgate for checkout based on:
     * 1. If the user has an active non-Solidgate subscription, keep Chargebee
     * 2. Everyone else uses Solidgate (including previously cancelled customers)
     */
    shouldUseSolidgate(): boolean {
      // 1. Keep Chargebee for active non-Solidgate subscribers (upsells/end-trial/payment updates)
      if (this.hasActiveNonSolidgateSubscription) {
        return false
      }

      // 2. For everyone else (new customers, cancelled customers, or existing Solidgate subscribers), use Solidgate
      return true
    },
  },

  actions: {
    async refreshAfterSubscriptionChange(): Promise<User | null> {
      // Central place for any post-subscription-change refresh logic
      return this.fetchDetails(true)
    },

    /**
     * Polls for a specific plan code to appear on the user.
     * Useful when upgrades/purchases rely on async PSP/webhook updates.
     */
    async waitForPlan(
      targetPlanCode: string | null | undefined,
      maxAttempts = 5,
      intervalMs = 2000
    ): Promise<void> {
      const normalizedTarget = targetPlanCode?.toLowerCase()

      // First check immediately
      await this.refreshAfterSubscriptionChange()

      if (
        !normalizedTarget ||
        this.highestPlan?.code?.toLowerCase() === normalizedTarget
      ) {
        return
      }

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        await this.refreshAfterSubscriptionChange()

        if (this.highestPlan?.code?.toLowerCase() === normalizedTarget) {
          return
        }

        if (attempt < maxAttempts) {
          await delay(intervalMs)
        }
      }

      throw new Error(
        `Plan ${normalizedTarget} not applied after ${maxAttempts} attempts`
      )
    },

    /**
     * Polls until the user has more entries than `since` (defaults to the
     * current count), into one draw or across all draws. Entries are
     * allocated asynchronously, so they can land a few seconds after the lead
     * or subscription that granted them.
     */
    async waitForEntries(
      drawCode?: string | null,
      since?: number,
      maxAttempts = 5,
      intervalMs = 2000
    ): Promise<boolean> {
      const count = () =>
        drawCode ? this.getDrawTicketCount(drawCode) : this.totalDrawTicketCount
      const baseline = since ?? count()

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          await this.fetchDetails(true)
        } catch {
          // Run in the background, so give up quietly.
          return false
        }

        if (count() > baseline) {
          return true
        }

        if (attempt < maxAttempts) {
          await delay(intervalMs)
        }
      }

      return false
    },

    /**
     * Polls until the user is no longer in trial.
     */
    async waitForTrialEnd(maxAttempts = 5, intervalMs = 2000): Promise<void> {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        await this.refreshAfterSubscriptionChange()

        if (!this.isInTrial) {
          return
        }

        if (attempt < maxAttempts) {
          await delay(intervalMs)
        }
      }

      throw new Error(`User is still in trial after ${maxAttempts} attempts`)
    },

    async logIn(
      email: string,
      password: string,
      rememberMe: boolean = true,
      include: string[] = ['csi_with_resource']
    ): Promise<User> {
      if (this.isLoggedIn) {
        return this.user as User
      }

      const includeParams = include.map((i) => `includes[${i}]=1`).join('&')
      const { data, error } = await fetcher.post<LoginResponse>(
        toPortalUrl(`/login?${includeParams}`),
        {
          email,
          password,
        },
        true
      )

      if (error.value) {
        throw error.value
      }

      const loginResponse = data.value as LoginResponse

      if (loginResponse.csi_content.redirect_url) {
        window.location.href = loginResponse.csi_content.redirect_url
        return this.user as User
      }

      const cookies = useCookies(['authToken'], {
        autoUpdateDependencies: true,
      })
      cookies.set('authToken', loginResponse.auth_token, {
        path: '/',
        expires: DateTime.now()
          .plus({ days: rememberMe ? 365 : 2 })
          .toJSDate(),
      })

      // Clear feature flags cache on login to ensure the user gets flags for their authenticated state
      const featureFlagsStore = useFeatureFlagsStore()
      featureFlagsStore.clearFeatureFlagsCache()

      // Immediately fetch new feature flags for authenticated user
      await featureFlagsStore.fetchFeatureFlags()

      const user = this.setUser(loginResponse.csi_content, password)

      // /login doesn't include the per-draw entry counts that /customers/me
      // does, so refresh in the background to fill them in.
      void this.fetchDetails(true).catch(() => {})

      return user
    },
    async magicLinkLogin(
      authLink: string,
      opts?: { force?: boolean }
    ): Promise<User> {
      const forceLogin = Boolean(opts?.force)

      if (this.isLoggedIn) {
        if (forceLogin) {
          try {
            await this.logOut(false)
          } catch (logoutError) {
            console.error(
              'Failed to clear existing session before magic-link login',
              logoutError
            )
          }
        } else {
          return this.user as User
        }
      }

      // Build the magic link URL directly without appending site_id, as
      // it is a signed URL and any extra query params break the signature.
      const magicLinkUrl = new URL(
        authLink,
        import.meta.env.GLP_PORTAL_URL
      ).toString()
      const { data, error } = await fetcher.get<LoginResponse>(
        magicLinkUrl,
        true, // useCredentials
        true // alwaysIncludeCredentials
      )

      if (error.value) {
        throw error.value
      }

      const loginResponse = data.value as LoginResponse

      if (loginResponse.csi_content.redirect_url) {
        window.location.href = loginResponse.csi_content.redirect_url
        return this.user as User
      }

      const cookies = useCookies(['authToken'], {
        autoUpdateDependencies: true,
      })
      cookies.set('authToken', loginResponse.auth_token, {
        path: '/',
        expires: DateTime.now().plus({ days: 365 }).toJSDate(),
      })

      // Clear feature flags cache on login to ensure the user gets flags for their authenticated state
      const featureFlagsStore = useFeatureFlagsStore()
      featureFlagsStore.clearFeatureFlagsCache()

      // Immediately fetch new feature flags for authenticated user
      await featureFlagsStore.fetchFeatureFlags()

      const user = this.setUser(loginResponse.csi_content)

      // /login doesn't include the per-draw entry counts that /customers/me
      // does, so refresh in the background to fill them in.
      void this.fetchDetails(true).catch(() => {})

      return user
    },
    async logOut(refreshPage: boolean = true): Promise<boolean> {
      const { error } = await fetcher.get(toPortalUrl('/logout'), true, false)

      if (error.value) {
        throw error.value
      }

      const cookies = useCookies(['authToken', 'impersonated'], {
        autoUpdateDependencies: true,
      })

      cookies.remove('authToken', { path: '/' })
      cookies.remove('impersonated', { path: '/' })

      // Clear feature flags cache on logout to ensure the user gets flags for their anonymous state
      const featureFlagsStore = useFeatureFlagsStore()
      featureFlagsStore.clearFeatureFlagsCache()

      // Immediately fetch new feature flags for anonymous user
      await featureFlagsStore.fetchFeatureFlags()

      this.user = null
      this.impersonationMode = null

      setGrowthBookUser(null)

      // Clean up all rewards subscriptions on logout
      await RewardToastSystem.cleanupAllSubscriptions()

      if (refreshPage) {
        this.$router.go(0) // Refresh the page
      }

      return true
    },
    async logInOrCreate(
      userData: Partial<User> & { email: string; password: string } & {
        includes?: { with_customer: boolean }
      }
    ): Promise<User> {
      if (this.isLoggedIn) {
        return this.user as User
      }

      const { error } = await fetcher.post<{
        id: string
        auth_token: string
      }>(toPortalUrl('/loginOrCreate?includes[csi]=1'), userData, true, true)

      if (error.value || !userData) {
        throw error.value ?? Error('An error occurred logging in or creating.')
      }

      let user = await this.logIn(userData.email, userData.password, true, [
        'csi_with_resource',
      ])

      user = {
        ...userData,
        ...user,
        new_registration: true,
      }

      this.user = user

      return user
    },
    async register(
      userData: Partial<User> & {
        email: string
        password: string
        first_name: string
        last_name: string
        phone: string
      }
    ): Promise<User> {
      if (this.isLoggedIn) {
        return this.user as User
      }

      const { error } = await fetcher.post<{
        id: string
        auth_token: string
      }>(toPortalUrl('/register?includes[csi]=1'), userData, true, true)

      if (error.value || !userData) {
        throw error.value ?? Error('An error occurred while registering.')
      }

      let user = await this.logIn(userData.email, userData.password, true, [
        'csi_with_resource',
      ])

      user = {
        ...userData,
        ...user,
        new_registration: true,
      }

      this.user = user

      return user
    },
    async setPasswordWithToken(userData: {
      email: string
      password: string
      password_confirmation: string
      token: string
      rememberMe: boolean
    }): Promise<User> {
      if (this.isLoggedIn) {
        return this.user as User
      }

      const { error } = await fetcher.post(
        toPortalUrl('/setPasswordWithToken'),
        userData,
        true
      )

      if (error.value || !userData) {
        throw error.value ?? Error('An error occurred creating user.')
      }

      let user = await this.logIn(
        userData.email,
        userData.password,
        userData.rememberMe,
        ['csi_with_resource']
      )

      user = {
        ...userData,
        ...user,
      }

      this.user = user

      return user
    },
    async checkAccountStatus(email: string): Promise<AccountStatus> {
      const { data, error } = await fetcher.post<AccountStatus>(
        toPortalUrl('/checkAccountStatus'),
        { email }
      )

      if (error.value || !data.value?.status) {
        throw error.value ?? Error('Unable to check account status.')
      }

      return data.value
    },
    async sendPasswordResetRequest(email: string): Promise<boolean> {
      const { error } = await fetcher.post(
        toPortalUrl('/requestPasswordReset'),
        {
          email,
          site: 'new',
        },
        true
      )

      if (error.value) {
        throw error.value
      }

      return true
    },
    /**
     * Update the user's password and logs them in.
     * @param {PasswordReset} data
     * @returns  {boolean}
     */
    async resetPassword(data: PasswordReset): Promise<boolean> {
      const { error } = await fetcher.post(toPortalUrl('/resetPassword'), data)

      if (error.value) {
        throw error.value
      }

      return await this.logOut(false)
    },
    async fetchDetails(forceLoad: boolean = false): Promise<User | null> {
      if (this.user && !forceLoad) {
        return this.user
      }

      const { data, error } = await fetcher.get(
        toPortalUrl('/customers/me?includes[csi_with_resource]=1'),
        true
      )

      if (error.value || !data.value) {
        throw (
          error.value ??
          Error(
            'A problem occurred fetching your details, please contact support.'
          )
        )
      }

      if (data.value.csi_content?.redirect_url) {
        window.location.href = data.value.csi_content.redirect_url
        return this.user
      }

      this.setUser(data.value.csi_content)

      return this.user
    },
    // TODO Fix return types from BE and setting here
    async fetchDetailsWithToken(
      email: string,
      key: string
    ): Promise<User & { [key: string]: any }> {
      const { data, error } = await fetcher.post<{ customer: User }>(
        toApiUrl('/payments/fetch'),
        {
          email,
          key,
        }
      )

      if (error.value) {
        throw error.value
      }

      return Object.assign(this.newEmptyUser.value, data.value?.customer ?? {})
    },
    setUser(details: LoginResponse['csi_content'], password?: string) {
      const periodMap = {
        month: 'monthly',
        year: 'yearly',
      } as const

      let updatedUser: User = {
        __type: 'User',
        city: details.city,
        country: details.country ?? Countries.UnitedKingdom,
        first_name: details.first_name,
        last_name: details.last_name,
        phone: details.phone,
        post_code: details.post_code ?? details.postal_code ?? '',
        referral_link: details.referral_link,
        referrals: [],
        referrer: false,
        region: details.region,
        status: details.status,
        address_line_1: details.address_line_1 ?? details.street1,
        address_line_2: details.address_line_2 ?? details.street2 ?? '',
        subscriber_id: details.subscriber_id,
        subscriptions: [],
        tickets: {},
        email: details.email,
        // The customer's account currency lock — used to charge in the correct
        // currency (e.g. one-click subscribe) rather than the build-time locale.
        currency: details.currency ?? null,
        has_active_pass: details.has_active_pass ?? false,
        id: details?.id,
        password: password ?? '',
        consent: false,
        contact_optin: false,
        credit: Object.assign(
          {
            totals: {
              overall: 0,
              cashable: 0,
              bonus: 0,
            },
          },
          details.credit
        ),
        loyalty: details.loyalty,
        tier: details.tier,
        // dob?: DateTime | string, 2017-05-15 09:12:34
        user_progression: details.user_progression,
        eligible_points: details.eligible_points ?? 0,
        frequency_game: details.frequency_game ?? { weekly_progress: [] },
        payment_methods: details.payment_methods ?? {},
        player_safety: details.player_safety ?? {},
      }

      if (details.referrals) {
        updatedUser = {
          ...updatedUser,
          referrals: details.referrals ?? [],
          referrer: details.referrer,
        }
      }

      if (details.subscriptions) {
        updatedUser = {
          ...updatedUser,
          subscriptions: details.subscriptions.map((subscription) => ({
            ...subscription,
            plan_period: subscription.plan_period
              ? periodMap[subscription.plan_period as keyof typeof periodMap]
              : subscription.plan_period,
          })),
        }
      }

      if (details.tier) {
        updatedUser = {
          ...updatedUser,
          tier: {
            ...details.tier,
            period: details.tier.period
              ? periodMap[details.tier.period as keyof typeof periodMap]
              : details.tier.period,
          },
        }
      }

      if (details.tickets) {
        updatedUser = {
          ...updatedUser,
          tickets: details.tickets,
        }
      }

      if (!this.user) {
        this.user = {} as User
      }

      if (details.player_safety) {
        const customerSafetyStore = useCustomerSafetyStore()

        for (const safetyMeasure of Object.values(details.player_safety)) {
          customerSafetyStore.customerSafety.set(
            safetyMeasure.__type,
            safetyMeasure
          )
        }
      }

      const isImpersonating = details.is_impersonating === true
      this.impersonationMode = isImpersonating
        ? (details.impersonation_mode ?? 'read_only')
        : null

      Object.assign(this.user, updatedUser)
      setGrowthBookUser(updatedUser)

      // Populate dailyGameProgress from user data if available
      if (details.frequency_game?.weekly_progress) {
        this.dailyGameProgress = details.frequency_game.weekly_progress
      }

      return updatedUser
    },
    async saveUser(userData: Partial<User> = {}) {
      const user = { ...this.user, ...userData }

      delete user.password

      const { error } = await fetcher.post(
        toPortalUrl(`customers/${user.id}`),
        user,
        true
      )

      if (error.value) {
        throw (
          error.value ??
          Error(
            'A problem occurred saving your details, please contact support.'
          )
        )
      }
    },

    async balanceWithdraw(value: number): Promise<boolean> {
      if (!this.user) {
        console.log('User must be logged in to withdraw.')
        return false
      }

      const { error } = await fetcher.post(
        toPortalUrl(`customers/cashout`),
        {
          value: value * 100,
        },
        true
      )

      if (error.value) {
        // Handle 403 errors specifically (wagering requirements not met)
        if (error.value.response?.status === 403) {
          const notificationStore = useNotificationStore()

          // Use backend message if available, otherwise provide friendly fallback
          const friendlyMessage = error.value.message
            ? error.value.message
            : 'Your withdrawal is temporarily locked due to active wagering requirements. Please complete your wagering requirements before requesting a withdrawal.'

          notificationStore.addNotification({
            type: 'withdrawal-locked',
            headline: 'Withdrawal Temporarily Locked',
            content_1: friendlyMessage,
            content_2:
              'Check your active bonuses in the deposit section to see your wagering progress.',
            buttons: {
              actions: [],
              dismiss: 'OK',
            },
          })

          // Refresh user details to update balance and any progress indicators
          await this.fetchDetails(true)

          return false
        }

        throw (
          error.value ??
          Error('A problem occurred withdrawing, please contact support.')
        )
      }

      // Minus the cashable balance from total
      this.user.credit.totals.cashable -= value * 100

      await this.fetchDetails(true)

      return true
    },

    async cancelWithdrawal(withdrawalId: number): Promise<void> {
      const { error } = await fetcher.post(
        toPortalUrl(`customers/withdrawals/${withdrawalId}/cancel`),
        {},
        true
      )

      if (error.value) {
        throw error.value ?? Error('Failed to cancel withdrawal.')
      }

      await this.fetchDetails(true)
    },

    /**
     * Update the user's credit.
     *
     * @param {string} creditType
     * @param {number} amount
     */
    updateUserCredit(creditType: string, amount: number) {
      if (!this.user) {
        console.log('User must be logged in to update credits.')
        return
      }

      if (!this.user.credit?.totals) {
        this.user.credit = {
          has_history: false,
          totals: {
            overall: 0,
            cashable: 0,
            bonus: 0,
            instant_win_cashable: 0,
          },
        }
      }

      switch (creditType) {
        case CreditTypes.Bonus:
          this.user.credit.totals.bonus += amount
          this.user.credit.totals.overall += amount
          break

        case CreditTypes.Cashable:
          this.user.credit.totals.cashable += amount
          this.user.credit.totals.overall += amount
          break

        case CreditTypes.InstantWinCashable:
          this.user.credit.totals.instant_win_cashable += amount
          this.user.credit.totals.cashable += amount
          this.user.credit.totals.overall += amount
          break
      }

      this.user.credit.has_history = true
    },

    batchUpdateUserCredit(
      updates: Array<{
        type: string
        amount: number
      }>
    ) {
      updates.forEach((update) => {
        this.updateUserCredit(update.type, update.amount)
      })
    },

    /**
     * Fetch the current credit balance for the user and update the store.
     *
     * @returns A promise that resolves with the credit balance data.
     */
    async fetchCreditBalance() {
      if (!this.user) {
        throw new Error('User must be logged in to fetch credit balance')
      }

      const { data: response, error } = await fetcher.get(
        toPortalUrl('/customers/creditBalance'),
        true
      )

      if (error.value) {
        throw new Error(
          error.value.message ||
            'An error occurred while fetching credit balance'
        )
      }

      // Update the user's credit data
      this.updateUserCreditFromResponse(response.value)

      return response.value
    },

    /**
     * Update the user's credit with data from the creditBalance API response.
     *
     * @param {object} creditBalanceResponse - Credit balance response from /customers/creditBalance
     */
    updateUserCreditFromResponse(creditBalanceResponse: CreditBalanceResponse) {
      if (!this.user) {
        return
      }
      if (creditBalanceResponse?.csi_content?.data) {
        const creditData = creditBalanceResponse.csi_content.data

        // Initialize credit object if it doesn't exist
        if (!this.user.credit) {
          this.user.credit = {
            has_history: false,
            totals: {
              overall: 0,
              cashable: 0,
              bonus: 0,
              instant_win_cashable: 0,
            },
          }
        }

        // Update credit data from API
        this.user.credit = {
          has_history: creditData.has_history || false,
          totals: {
            overall: creditData.totals?.overall || 0,
            cashable: creditData.totals?.cashable || 0,
            bonus: creditData.totals?.bonus || 0,
            instant_win_cashable: creditData.totals?.instant_win_cashable || 0,
          },
        }
      }
    },

    /**
     * Updates the user tickets with recently purchased instant wins data.
     *
     * If no purchase ID exists. No update occurs.
     *
     * @param {User} userData
     * @param {InstantWinDraw} instantWinDraw
     * @param {InstantWinDrawTickets} instantWinDrawTickets
     * @returns
     */
    setInstantWinTickets(
      instantWinDraw: InstantWinDraw,
      instantWinDrawTickets: InstantWinDrawTickets
    ) {
      this.latestInstantWinEntries.set(instantWinDraw.code, {
        draw_code: instantWinDraw.code,
        purchased_at: DateTime.now(),
        is_winner: instantWinDrawTickets.is_winner ?? false,
        winning_entries: instantWinDrawTickets.winning_entries ?? [],
        non_winning_entries: instantWinDrawTickets.non_winning_entries ?? [],
        purchase_id: instantWinDrawTickets.purchase_id,
      })
    },
    getLatestInstantWinTickets(
      instantWinDraw?: InstantWinDraw
    ): Nullable<InstantWinDrawTickets> {
      if (!instantWinDraw) {
        const latestInstantWinEntries = Array.from(
          this.latestInstantWinEntries.values()
        )

        return latestInstantWinEntries.length
          ? latestInstantWinEntries?.[0]
          : null
      }

      return this.latestInstantWinEntries.get(instantWinDraw.code) ?? null
    },

    getSubscriptionById(id: number): Nullable<Membership> {
      return (
        this.user?.subscriptions?.find((subscription: Membership) => {
          return subscription.id === id
        }) ?? null
      )
    },

    /**
     * Fetches the user's credit history paginated.
     *
     * @param {number} page The page to fetch.
     * @returns {Promise<PaginatedCreditHistory>} The paginated credit history.
     */
    async fetchCreditHistory(
      page: number = 1
    ): Promise<PaginatedCreditHistory> {
      if (this.creditHistory.has(page)) {
        return this.creditHistory.get(page) as PaginatedCreditHistory
      }

      const { error, data } = await fetcher.get<{
        csi_content: PaginatedCreditHistory
      }>(toPortalUrl(`/customers/balanceHistory?page=${page}`), true)

      const creditHistory = data.value?.csi_content

      if (error.value || !creditHistory) {
        throw new Error(error.value ?? 'Failed to load transaction history')
      }

      this.creditHistory.set(page, creditHistory)

      return creditHistory
    },

    async fetchDailyGameProgress(): Promise<DailySpinProgress[]> {
      const { data, error } = await fetcher.get<DailySpinProgressResponse>(
        toPortalUrl('/daily-game/weekly-progress')
      )

      if (error.value) {
        throw error.value ?? 'Failed to load user daily game progress'
      }

      this.dailyGameProgress = data.value?.csi_content.data ?? []

      return this.dailyGameProgress
    },

    /**
     * Fetches the entries to a specific draw for the current user. The
     * resulting data (an array) will contain a summary of ticket types and the
     * related count. (E.g Subscription, Loyalty and Lead tickets for prize
     * draws or "Purchases" for Instant Prizes)
     * @param code the drawcode of the draw to fetch data for
     */
    async fetchEntryDetailsByDrawCode(
      code: string
    ): Promise<EntryDetailItem[]> {
      const params = new URLSearchParams({ drawCode: code })
      const url = toPortalUrl(`customers/tickets/types?${params}`)
      const { data, error } = await fetcher.get<EntryDetailResponse>(url)
      if (error.value) {
        throw error.value ?? 'Failed to load draw entry summary'
      }
      return data.value?.csi_content ?? []
    },

    async fetchBalanceBreakdown(): Promise<BalanceSummary> {
      const url = toPortalUrl(`customers/transactionSummary`)
      const { data, error } = await fetcher.get<BalanceSummaryResponse>(url)
      if (error.value || !data.value?.csi_content) {
        throw error.value ?? 'Failed to load balance summary'
      }
      return data.value?.csi_content
    },

    /**
     * Gets the total number of tickets the user has for a specific draw code.
     * Counts returned from the CustomerResource.
     *
     * @param  {string} drawCode The draw code to get ticket count for
     * @returns {number} Total ticket count for the draw code
     */
    getDrawTicketCount(drawCode: string): number {
      return Object.entries(this.drawTickets ?? {}).reduce(
        (acc, [code, ticket]: [string, Ticket]) => {
          if (code === drawCode) {
            acc += ticket.count ?? 0
          }
          return acc
        },
        0
      )
    },

    /**
     * Fetches the user's prize fulfilments along with associated prize details.
     *
     * @returns {Promise<PrizeFulfilmentWithPrize[]>} The prize fulfilments with prize details.
     */
    async fetchPrizeFulfilments(
      force: boolean = false
    ): Promise<PrizeFulfilmentWithPrize[]> {
      if (!force && this.prizeFulfilments.size > 0) {
        return Array.from(this.prizeFulfilments.values())
      }

      const { data, error } = await fetcher.get<
        CsiResponse<{ fulfilments: PrizeFulfilment[] }>
      >(toPortalUrl('customers/prize-fulfilments/list'))

      if (error.value) {
        throw new Error(
          error.value ??
            'Failed to load prizes. Please try again or contact support if the problem persists.'
        )
      }

      this.prizeFulfilments.clear()

      const prizeFulfilments = await this.setFulfilmentPrizes(
        data.value?.csi_content.fulfilments ?? []
      )

      for (const fulfilment of prizeFulfilments) {
        this.prizeFulfilments.set(fulfilment.ticket_number, fulfilment)
      }

      return Array.from(this.prizeFulfilments.values())
    },

    /**
     * Claims a prize fulfilment for the user and updates the store with the claimed prize details.
     * The user's credit balance is also refreshed after claiming.
     *
     * @param {string} fulfilmentId
     * @param {ClaimType} claimType
     * @returns {Promise<PrizeFulfilmentWithPrize>}
     */
    async claimPrizeFulfilment(
      fulfilmentId: string,
      claimType: ClaimType
    ): Promise<PrizeFulfilmentWithPrize> {
      const userStore = useUserStore()

      const { data, error } = await fetcher.post<CsiResponse<PrizeFulfilment>>(
        toPortalUrl('customers/prize-fulfilments/claim'),
        {
          fulfilmentId,
          claimType,
        }
      )

      const fulfilment = data.value?.csi_content

      if (error.value || !fulfilment) {
        throw new Error(
          error.value ??
            'Failed to claim prize.  Please try again or contact support if the problem persists.'
        )
      }

      const prizeFulfilment = (await this.setFulfilmentPrizes([fulfilment]))[0]

      this.prizeFulfilments.set(fulfilment.ticket_number, prizeFulfilment)

      void userStore.fetchCreditBalance()

      return this.prizeFulfilments.get(
        fulfilment.ticket_number
      ) as PrizeFulfilmentWithPrize
    },

    async setFulfilmentPrizes(
      prizeFulfilments: PrizeFulfilment[]
    ): Promise<PrizeFulfilmentWithPrize[]> {
      const instantWinStore = useInstantWinsStore()

      const prizeFulfilmentsWithPrizes: PrizeFulfilmentWithPrize[] = []

      const prizes = (
        await Promise.all([
          ...prizeFulfilments.map((fulfilment: PrizeFulfilment) =>
            instantWinStore.getInstantWin(fulfilment.draw_code)
          ),
        ])
      )
        .filter((prize): prize is NonNullable<typeof prize> => prize !== null)
        // Cast as Prize since both Prize and InstantWin types have properties required
        .map(
          (prize) =>
            (isPrize(prize) ? prize : prize.main_prize) as unknown as Prize
        )

      for (const fulfilment of prizeFulfilments) {
        const prize = prizes.find(
          (prize) => prize.code === fulfilment.draw_code
        )

        if (!prize) {
          console.warn(
            `Draw with code ${fulfilment.draw_code} not found for fulfilment`,
            fulfilment
          )
          continue
        }

        prizeFulfilmentsWithPrizes.push({
          ...fulfilment,
          prize,
        })
      }

      return prizeFulfilmentsWithPrizes
    },

    /**
     * Fetches the user's unplayed game sessions for instant win games.
     *
     * @returns {Promise<Array<{ game_session: GameSessionData }>>} An array of unplayed game session data.
     */
    async fetchUnplayedGameSessions(): Promise<
      Array<{ game_session: GameSessionData }>
    > {
      const { data, error } = await fetcher.get<
        CsiResponse<{
          data: { game_sessions: Array<{ game_session: GameSessionData }> }
        }>
      >(toPortalUrl('instantwin/game/sessions?status=active'), true)

      if (error.value) {
        return []
      }

      return data.value?.csi_content.data.game_sessions ?? []
    },

    subscribeToProgressMilestoneBroadcast() {
      if (!this.user?.id) {
        return
      }

      RealTimeEvents.subscribe(
        RealTimeEvents.getChannel('ProgressMilestone', this.user.id),
        RealTimeEvents.Events.ProgressMilestoneUpdate,
        true,
        (eventData: ProgressMilestoneUpdateEvent) => {
          let message = ''

          switch (eventData.milestone_name) {
            case 'DAILY_GAME_STREAK_7_DAYS': {
              const currentDayOfTheWeek = DateTime.now().weekday
              const currentStreak =
                this.dailyGameProgress.findIndex(
                  (progress) => progress.day_number === currentDayOfTheWeek
                ) + 1

              message = `Congratulations! Daily Game ${currentStreak} Day Streak achieved! Your Reward Points were credited!`
              break
            }
            case 'INSTANT_GAMES_PLAYED_5':
              message = `Congratulations! You played 5 Instant Win games! Your Reward Points were credited!`
              break
            case 'INSTANT_GAMES_PLAYED_10':
              message = `Amazing! You've played 10 Instant Win games! Your Reward Points were credited!`
              break
          }

          if (this.user && message) {
            const toast = useToast()

            toast.info(message)

            this.user.eligible_points += eventData.xp_awarded
          }
        }
      )
    },

    unsubscribeFromProgressMilestoneBroadcast() {
      if (!this.user?.id) {
        return
      }

      RealTimeEvents.unsubscribe(
        RealTimeEvents.getChannel('ProgressMilestone', this.user?.id),
        RealTimeEvents.Events.ProgressMilestoneUpdate
      )
    },
  },
})

// Watch for authentication state changes and manage rewards subscriptions
// This is now handled by setupAuthWatchers() which should be called after Pinia is active
